import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Link,
} from '@mui/material';
import { ArrowBack, Edit, Refresh, OpenInNew } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { format, subDays } from 'date-fns';
import { adsService } from '../../services/ads.service';
import CreativePreviewDialog from '../../components/CreativePreviewDialog';
import { adsetsService } from '../../services/adsets.service';
import { insightsService } from '../../services/insights.service';
import StatusBadge from '../../components/StatusBadge';
import { HistoryButton } from '../../components/EntityHistory';

interface UpdateAdRequest {
  name?: string;
  status?: string;
  headline?: string;
  body?: string;
  call_to_action?: string;
  link_url?: string;
}

const callToActions = [
  { value: 'LEARN_MORE', label: 'Learn More' },
  { value: 'SHOP_NOW', label: 'Shop Now' },
  { value: 'SIGN_UP', label: 'Sign Up' },
  { value: 'SUBSCRIBE', label: 'Subscribe' },
  { value: 'CONTACT_US', label: 'Contact Us' },
  { value: 'DOWNLOAD', label: 'Download' },
  { value: 'GET_QUOTE', label: 'Get Quote' },
  { value: 'BOOK_NOW', label: 'Book Now' },
  { value: 'APPLY_NOW', label: 'Apply Now' },
  { value: 'GET_OFFER', label: 'Get Offer' },
  { value: 'NO_BUTTON', label: 'No Button' },
];

export default function AdDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editData, setEditData] = useState<UpdateAdRequest>({});
  const [insightsDateRange] = useState({
    date_from: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    date_to: format(new Date(), 'yyyy-MM-dd'),
  });

  const { data: ad, isLoading } = useQuery({
    queryKey: ['ad', id],
    queryFn: () => adsService.getById(id!),
    enabled: !!id,
  });

  const { data: adSet } = useQuery({
    queryKey: ['adset', ad?.ad_set_id],
    queryFn: () => adsetsService.getById(ad!.ad_set_id),
    enabled: !!ad?.ad_set_id,
  });

  const { data: insights, isLoading: insightsLoading, refetch: refetchInsights } = useQuery({
    queryKey: ['ad-insights', id, insightsDateRange],
    queryFn: () => insightsService.getAdInsights(id!, insightsDateRange),
    enabled: !!id && tab === 1,
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateAdRequest) => adsService.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ad', id] });
      queryClient.invalidateQueries({ queryKey: ['ads'] });
      setEditDialogOpen(false);
    },
  });


  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="text" width={300} height={50} />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (!ad) {
    return (
      <Box>
        <Alert severity="error">Ad not found</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/ads')} sx={{ mt: 2 }}>
          Back to Ads
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(`/adsets/${ad.ad_set_id}`)}
          sx={{ mr: 2 }}
        >
          Back to Ad Set
        </Button>
        <Typography variant="h4" fontWeight={600} sx={{ flexGrow: 1 }}>
          {ad.name}
        </Typography>
        <Box sx={{ mr: 2 }}>
          <StatusBadge
            status={ad.status}
            effectiveStatus={ad.effective_status}
            staleSince={ad.status_stale_since}
          />
          <HistoryButton entityType="ads" entityId={ad.id} entityName={ad.name} />
        </Box>
        <Button variant="contained" startIcon={<Edit />} onClick={() => navigate(`/ads/${id}/edit`)}>
          Edit Ad
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Ad Set
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {ad.ad_set?.name || adSet?.name || '-'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Campaign
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {ad.campaign?.name || '-'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Ad Account
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {ad.ad_account?.name || '-'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Facebook Ad ID
              </Typography>
              <Typography variant="body1" fontWeight={500} sx={{ fontFamily: 'monospace' }}>
                {ad.fb_ad_id || 'Not published'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Call to Action
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {callToActions.find((c) => c.value === ad.call_to_action)?.label || ad.call_to_action || '-'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Created
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {new Date(ad.created_at).toLocaleDateString()}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Creative" />
        <Tab label="Insights" />
      </Tabs>

      {tab === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Ad Creative
            </Typography>
            <Grid container spacing={3}>
              {/* Thumbnail / media preview */}
              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {ad.creative_image_url ? (
                    <Box
                      component="img"
                      src={ad.creative_image_url}
                      alt="Creative thumbnail"
                      onClick={() => setPreviewOpen(true)}
                      sx={{
                        width: 200,
                        height: 200,
                        objectFit: 'cover',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        cursor: 'pointer',
                        '&:hover': { opacity: 0.8 },
                      }}
                      onError={(e: any) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <Box
                      onClick={() => ad.fb_ad_id && setPreviewOpen(true)}
                      sx={{
                        width: 200, height: 200, borderRadius: 2,
                        border: '2px dashed', borderColor: 'divider',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: ad.fb_ad_id ? 'pointer' : 'default',
                      }}
                    >
                      <Typography variant="caption" color="text.disabled">
                        {ad.fb_ad_id ? 'Click to load preview' : 'No preview'}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    {ad.fb_video_id && (
                      <Alert severity="info" sx={{ mb: 1 }}>
                        Video ad (FB video ID: {ad.fb_video_id})
                      </Alert>
                    )}
                    {ad.fb_image_hash && !ad.fb_video_id && (
                      <Alert severity="info" sx={{ mb: 1 }}>
                        Image ad (FB hash: {ad.fb_image_hash})
                      </Alert>
                    )}
                    {ad.fb_creative_id && (
                      <Typography variant="caption" color="text.secondary">
                        Creative ID: {ad.fb_creative_id}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary">
                  {ad.headlines?.length ? `Headlines (${ad.headlines.length})` : 'Headline'}
                </Typography>
                {ad.headlines?.length ? (
                  ad.headlines.map((h: string, i: number) => (
                    <Typography key={i} variant="h6" fontWeight={500}>{h}</Typography>
                  ))
                ) : (
                  <Typography variant="h6" fontWeight={500}>{ad.headline || '-'}</Typography>
                )}
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary">
                  {ad.bodies?.length ? `Primary Texts (${ad.bodies.length})` : 'Primary Text'}
                </Typography>
                {ad.bodies?.length ? (
                  ad.bodies.map((b: string, i: number) => (
                    <Typography key={i} variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 0.5 }}>{b}</Typography>
                  ))
                ) : (
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{ad.body || '-'}</Typography>
                )}
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary">
                  Destination URL
                </Typography>
                {ad.link_url ? (
                  <Link
                    href={ad.link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    {ad.link_url}
                    <OpenInNew fontSize="small" />
                  </Link>
                ) : (
                  <Typography variant="body1">-</Typography>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {tab === 1 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Performance Metrics ({insightsDateRange.date_from} to {insightsDateRange.date_to})
              </Typography>
              <Button
                startIcon={<Refresh />}
                onClick={() => refetchInsights()}
                disabled={insightsLoading}
              >
                Refresh
              </Button>
            </Box>
            {insightsLoading ? (
              <Grid container spacing={3}>
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                    <Skeleton variant="rectangular" height={100} />
                  </Grid>
                ))}
              </Grid>
            ) : insights && insights.length > 0 ? (
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        Impressions
                      </Typography>
                      <Typography variant="h5" fontWeight={600}>
                        {insights.reduce((sum, i) => sum + (i.impressions || 0), 0).toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        Clicks
                      </Typography>
                      <Typography variant="h5" fontWeight={600}>
                        {insights.reduce((sum, i) => sum + (i.clicks || 0), 0).toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        Spend
                      </Typography>
                      <Typography variant="h5" fontWeight={600}>
                        ${(insights.reduce((sum, i) => sum + (i.spend || 0), 0) / 100).toFixed(2)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        Reach
                      </Typography>
                      <Typography variant="h5" fontWeight={600}>
                        {insights.reduce((sum, i) => sum + (i.reach || 0), 0).toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        CTR
                      </Typography>
                      <Typography variant="h5" fontWeight={600}>
                        {(() => {
                          const totalImpressions = insights.reduce((sum, i) => sum + (i.impressions || 0), 0);
                          const totalClicks = insights.reduce((sum, i) => sum + (i.clicks || 0), 0);
                          return totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : '0.00';
                        })()}%
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        CPC
                      </Typography>
                      <Typography variant="h5" fontWeight={600}>
                        ${(() => {
                          const totalSpend = insights.reduce((sum, i) => sum + (i.spend || 0), 0);
                          const totalClicks = insights.reduce((sum, i) => sum + (i.clicks || 0), 0);
                          return totalClicks > 0 ? ((totalSpend / 100) / totalClicks).toFixed(2) : '0.00';
                        })()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            ) : (
              <Typography color="text.secondary">
                No insights data available for this ad. Sync data from your ad account to see metrics.
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Edit Ad Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Ad</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Ad Name"
              fullWidth
              value={editData.name || ''}
              onChange={(e) => setEditData({ ...editData, name: e.target.value })}
            />
            <FormControl fullWidth>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Status
              </Typography>
              <ToggleButtonGroup
                value={editData.status}
                exclusive
                onChange={(_, value) => value && setEditData({ ...editData, status: value })}
                fullWidth
              >
                <ToggleButton value="PAUSED">Paused</ToggleButton>
                <ToggleButton value="ACTIVE">Active</ToggleButton>
              </ToggleButtonGroup>
            </FormControl>
            <TextField
              label="Headline"
              fullWidth
              value={editData.headline || ''}
              onChange={(e) => setEditData({ ...editData, headline: e.target.value })}
            />
            <TextField
              label="Primary Text"
              fullWidth
              multiline
              rows={4}
              value={editData.body || ''}
              onChange={(e) => setEditData({ ...editData, body: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Call to Action</InputLabel>
              <Select
                value={editData.call_to_action || ''}
                label="Call to Action"
                onChange={(e) => setEditData({ ...editData, call_to_action: e.target.value })}
              >
                {callToActions.map((cta) => (
                  <MenuItem key={cta.value} value={cta.value}>
                    {cta.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Destination URL"
              fullWidth
              type="url"
              value={editData.link_url || ''}
              onChange={(e) => setEditData({ ...editData, link_url: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => updateMutation.mutate(editData)}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <CreativePreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        adId={ad?.id || ''}
      />
    </Box>
  );
}
